/**
 * Shared Stripe helpers reused across Stripe payment flows (TDC and wallets such as Google Pay).
 *
 * This module is intentionally dependency-free and side-effect-free so any Stripe action
 * (`stripe.js`, `stripeGooglePay.js`, …) can reuse the same result-normalization contract without
 * coupling to a specific payment method.
 */

/**
 * Normalize a Stripe payment response into a hook-friendly contract.
 *
 * With `options.clientConfirmation` (express wallets: the backend creates the PaymentIntent
 * UNCONFIRMED and the client finalizes it with `stripe.confirmPayment`), a pending response
 * carrying `payload.clientSecret` maps to `requires_client_confirmation`. The flag is explicit
 * context from the wallet action — `payload.using3dSecure` cannot discriminate this flow because
 * the backend also sets it on pending wallet payments. A response that already settled (the
 * purchase left `attempt`, or the attempt is charged/failed/rejected) never maps to a client
 * action, however: it falls through to the terminal outcomes below so the backend's own result
 * reaches the user instead of a Stripe error about an unexpected PaymentIntent state.
 *
 * @param {Object} camelizedResponse - The camelized payment response.
 * @param {Object} [options]
 * @param {boolean} [options.clientConfirmation] - The payment is confirmed on the client
 *   (express wallets); map clientSecret-bearing pending responses to
 *   `requires_client_confirmation` instead of the TDC 3DS outcome.
 * @returns {{
 *   outcome: 'completed'|'pending'|'requires_client_action'|'requires_client_confirmation'|'failed',
 *   clientAction?: {
 *     type: 'stripe_next_action'|'stripe_confirm_payment',
 *     clientSecret: string,
 *     paymentIntentId?: string,
 *   },
 *   errorReason?: string,
 *   raw: Object,
 * }}
 */
export function normalizeStripeResult(camelizedResponse = {}, options = {}) {
  const { status, payload = {} } = camelizedResponse;
  const responseStatus = status || (camelizedResponse.polls ? 'pending' : undefined);
  const errorReason = payload.errorReason || 'default';
  // The response is settled once the purchase left the `attempt` state or the attempt itself
  // reached a terminal status. Confirming a settled PaymentIntent is never right: Stripe answers
  // with a generic `payment_intent_unexpected_state` error that replaces the backend's real
  // decline reason, and the flow skips the terminal routing `paymentPollingHandler` owns.
  const isSettled =
    payload.purchaseState !== 'attempt' ||
    ['charged', 'failed', 'rejected'].includes(payload.status);
  // Wallet responses always carry the clientSecret the client confirmation needs, so this flag —
  // not `using3dSecure`, which the backend also sets on wallets — is what claims the response for
  // the wallet flow and keeps it out of the TDC 3DS branch below.
  const wantsClientConfirmation = Boolean(options.clientConfirmation && payload.clientSecret);

  if (wantsClientConfirmation && !isSettled) {
    return {
      outcome: 'requires_client_confirmation',
      clientAction: {
        type: 'stripe_confirm_payment',
        clientSecret: payload.clientSecret,
        paymentIntentId: payload.paymentIntentId,
      },
      errorReason,
      raw: camelizedResponse,
    };
  }

  if (!wantsClientConfirmation && payload.using3dSecure === true && payload.clientSecret) {
    return {
      outcome: 'requires_client_action',
      clientAction: {
        type: 'stripe_next_action',
        clientSecret: payload.clientSecret,
      },
      errorReason,
      raw: camelizedResponse,
    };
  }

  if (payload.purchaseState !== 'attempt' || payload.status === 'charged') {
    return {
      outcome: 'completed',
      errorReason,
      raw: camelizedResponse,
    };
  }

  if (
    !['pending', 'completed', 'aborted'].includes(responseStatus) ||
    ['failed', 'rejected'].includes(payload.status)
  ) {
    return {
      outcome: 'failed',
      errorReason,
      raw: camelizedResponse,
    };
  }

  return {
    outcome: 'pending',
    errorReason,
    raw: camelizedResponse,
  };
}

export default normalizeStripeResult;
